import { Instagram, Baby, LineChart, ShieldCheck, BookOpen, HeartHandshake, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const CATEGORIES = [
  { icon: Baby, label: "Newborn Care" },
  { icon: LineChart, label: "Child Growth & Development" },
  { icon: ShieldCheck, label: "Immunization" },
  { icon: BookOpen, label: "Parenting Education" },
  { icon: HeartHandshake, label: "Child Health Awareness" },
];

const LittleOnes = () => (
  <section id="little-ones" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="relative bg-[#F7ECF0] border border-[#E8C2CB]/60 rounded-[32px] px-6 sm:px-12 lg:px-16 py-14 sm:py-20 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#E8C2CB]/45 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-28 -right-20 w-80 h-80 rounded-full bg-white/60 blur-3xl" aria-hidden="true" />

        <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Reveal>
              <p data-testid="little-ones-brand" className="mb-6">
                <span className="font-display italic text-4xl sm:text-5xl text-[#70435F] font-semibold">Little Ones</span>
                <span className="block text-xs uppercase tracking-[0.24em] font-semibold text-[#8A7E88] mt-2">
                  by Dr. Ojaswini
                </span>
              </p>
              <h2
                data-testid="little-ones-heading"
                className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#383237] leading-tight mb-6"
              >
                Thoughtful guidance for <em className="italic text-[#70435F]">healthier childhoods.</em>
              </h2>
              <p data-testid="little-ones-description" className="text-base text-[#695E67] leading-relaxed mb-8 max-w-lg">
                Childhood is a foundation for lifelong health. Through Little Ones, Dr. Ojaswini shares
                accessible educational content on preventive pediatrics, parenting, newborn care, and child
                health awareness.
              </p>
              <a
                data-testid="little-ones-instagram-link"
                href="https://www.instagram.com/littleonesdrojaswini/"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-[#70435F] text-white pl-5 pr-6 py-3.5 rounded-full hover:bg-[#5A344C] transition-colors shadow-soft font-medium text-sm"
              >
                <Instagram size={18} strokeWidth={1.7} />
                Follow @littleonesdrojaswini
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7E88] mb-4">
              Education themes · Instagram grid ready for future content
            </p>
            <div data-testid="little-ones-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {CATEGORIES.map((c) => (
                <div
                  key={c.label}
                  className="aspect-square bg-white/70 border border-dashed border-[#D6A3AF]/60 rounded-2xl flex flex-col items-center justify-center gap-3 text-center px-3 transition-all duration-500 hover:bg-white hover:-translate-y-1 hover:shadow-card"
                >
                  <c.icon size={24} strokeWidth={1.4} className="text-[#70435F]" />
                  <span className="text-xs font-semibold text-[#383237] leading-snug">{c.label}</span>
                </div>
              ))}
              <a
                data-testid="little-ones-grid-cta"
                href="https://www.instagram.com/littleonesdrojaswini/"
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-square bg-[#70435F] rounded-2xl flex flex-col items-center justify-center gap-3 text-center px-3 transition-all duration-500 hover:bg-[#5A344C] hover:-translate-y-1 hover:shadow-soft"
              >
                <Instagram size={24} strokeWidth={1.4} className="text-[#E8C2CB]" />
                <span className="text-xs font-semibold text-white leading-snug">New posts on Instagram</span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="relative mt-14 max-w-2xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7E88] flex items-center gap-2">
              <Instagram size={14} strokeWidth={1.7} className="text-[#70435F]" />
              Live from Instagram
            </p>
            <a
              data-testid="little-ones-live-profile-link"
              href="https://www.instagram.com/littleonesdrojaswini/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-[#70435F] hover:underline"
            >
              View profile
            </a>
          </div>
          <div className="bg-white border border-[#E8C2CB]/60 rounded-3xl p-2.5 sm:p-3 shadow-card overflow-hidden">
            <iframe
              data-testid="little-ones-instagram-embed"
              title="Latest Instagram posts from @littleonesdrojaswini"
              src="https://www.instagram.com/littleonesdrojaswini/embed/"
              className="w-full h-[380px] sm:h-[440px] rounded-2xl border-0"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default LittleOnes;
